import { CommonModule } from '@angular/common';
import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { MaterialModule } from '../material.module';
import {
  YearlyCalibrationCertificate,
  YearlyCalibrationCertificateRequest,
} from './yearly-calibration-certificates.models';
import { YearlyCalibrationCertificatesService } from './yearly-calibration-certificates.service';

export interface YearlyCalibrationCertificateFormDialogData {
  certificate: YearlyCalibrationCertificate | null;
}

@Component({
  selector: 'app-yearly-calibration-certificate-form-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule],
  templateUrl: './yearly-calibration-certificate-form-dialog.component.html',
  styleUrl: './yearly-calibration-certificate-form-dialog.component.scss',
})
export class YearlyCalibrationCertificateFormDialogComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly service = inject(YearlyCalibrationCertificatesService);
  readonly dialogRef = inject(MatDialogRef<YearlyCalibrationCertificateFormDialogComponent>);
  readonly data = inject<YearlyCalibrationCertificateFormDialogData>(MAT_DIALOG_DATA);
  readonly form = this.formBuilder.nonNullable.group({
    date: [this.data.certificate?.date ?? this.today(), Validators.required],
    name: [this.data.certificate?.name ?? '', [Validators.required, Validators.maxLength(200)]],
    description: [this.data.certificate?.description ?? '', [Validators.required, Validators.maxLength(4000)]],
    note: [this.data.certificate?.note ?? '', Validators.maxLength(4000)],
  });

  selectedFile: File | null = null;
  isDragOver = false;
  isSaving = false;
  validationAttempted = false;
  uploadProgress: number | null = null;
  errorMessage = '';

  get isEditing(): boolean {
    return this.data.certificate !== null;
  }

  fileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.setSelectedFile(input.files?.[0] ?? null);
  }

  onFileDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isSaving) return;
    this.isDragOver = true;
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }

  onFileDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const dropZone = event.currentTarget;
    if (dropZone instanceof HTMLElement
        && event.relatedTarget instanceof Node
        && dropZone.contains(event.relatedTarget)) {
      return;
    }
    this.isDragOver = false;
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    if (this.isSaving) return;
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (files.length > 1) {
      this.selectedFile = null;
      this.errorMessage = 'Attach one certificate file at a time.';
      return;
    }
    this.setSelectedFile(files[0] ?? null);
  }

  openDatePicker(input: HTMLInputElement): void {
    input.showPicker?.();
  }

  save(): void {
    if (this.isSaving) return;

    this.validationAttempted = true;
    this.form.markAllAsTouched();
    if (this.form.invalid) return;
    if (!this.isEditing && !this.selectedFile) {
      this.errorMessage = 'Choose a certificate file before saving.';
      return;
    }
    if (this.selectedFile && !this.isSupportedFile(this.selectedFile)) {
      this.errorMessage = 'Choose a PDF, JPG/JPEG, or PNG file no larger than 25 MB.';
      return;
    }

    const formValue = this.form.getRawValue();
    const request: YearlyCalibrationCertificateRequest = {
      date: formValue.date,
      name: formValue.name.trim(),
      description: formValue.description.trim(),
      note: formValue.note.trim() || null,
    };
    const operation = this.data.certificate
      ? this.service.updateCertificate(this.data.certificate.id, request, this.selectedFile ?? undefined)
      : this.service.createCertificate(request, this.selectedFile!);

    this.isSaving = true;
    operation.pipe(finalize(() => this.isSaving = false)).subscribe({
      next: event => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          this.uploadProgress = Math.round((event.loaded / event.total) * 100);
        }
        if (event.type === HttpEventType.Response) {
          if (!event.body) {
            this.errorMessage = 'The API did not return the saved certificate.';
            return;
          }
          this.dialogRef.close(event.body);
        }
      },
      error: error => this.errorMessage = this.getErrorMessage(error),
    });
  }

  private isSupportedFile(file: File): boolean {
    if (file.size <= 0 || file.size > 25 * 1024 * 1024) return false;
    const extension = file.name.split('.').pop()?.toLowerCase();
    const allowedTypes: Record<string, string> = {
      pdf: 'application/pdf',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
    };
    return Boolean(extension && allowedTypes[extension] === file.type.toLowerCase());
  }

  private setSelectedFile(file: File | null): void {
    this.selectedFile = file;
    this.errorMessage = '';
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const body = error.error as { errors?: Record<string, string[]>; title?: string } | null;
      const validationMessage = body?.errors ? Object.values(body.errors).flat()[0] : undefined;
      return validationMessage ?? body?.title ?? 'The yearly calibration certificate could not be saved.';
    }
    return 'The yearly calibration certificate could not be saved.';
  }

  private today(): string {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  }
}
